import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import config from '../config';
import { Settings, BarChart2 } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod as bornesDeComparaison } from '../utils/periodUtils';
/**
 * Composant pour afficher la distribution des paliers de CPC avec conversions/coûts/COS
 */
const CpcTierBreakdown = ({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  onTierClick,
  selectedCpcTierFilter = null,
  selectedCampaignFilter,
  selectedQualityScoreFilter,
  selectedMatchTypeFilter,
  token,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = ''
}) => {
  const [tierData, setTierData] = useState(null);
  const [previousTierData, setPreviousTierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'totalCost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Calcul de la période précédente
  const calculatePreviousPeriod = useCallback(() => {
    if (!comparisonType || !selectedPeriod) return null;

    // Les bornes de la période regardée, puis celles auxquelles on la compare. Les deux
    // calculs vivaient ici, recopiés dans une vingtaine d'écrans : ils sont dans
    // `utils/periodUtils`, seul endroit à savoir ce que « hier » veut dire un jour de
    // changement d'heure.
    const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
    if (!plage) return null;

    return bornesDeComparaison(plage.startDate, plage.endDate, comparisonType);
  }, [comparisonType, selectedPeriod, customStartDate, customEndDate]);


  // Affichage de la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Map pour accéder rapidement aux données de la période précédente
  const previousTiersMap = useMemo(() => {
    if (!compareEnabled || !previousTierData?.tiers) return {};
    const map = {};
    previousTierData.tiers.forEach(tier => {
      const key = `${tier.cpcMin}-${tier.cpcMax}`;
      map[key] = tier;
    });
    return map;
  }, [compareEnabled, previousTierData?.tiers]);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('keywords_cpc_tiers', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Fonction pour calculer les paliers CPC côté client (optimisée pour gros datasets)
  const calculateCpcTiers = (keywords) => {
    // Filter by campaign first if a filter is active
    let filteredKeywords = keywords;
    if (selectedCampaignFilter !== null && selectedCampaignFilter !== undefined) {
      filteredKeywords = keywords.filter(keyword => {
        return keyword.campaignName === selectedCampaignFilter.id;
      });
    }
    
    // Filter by quality score if a filter is active
    if (selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        const keywordScore = keyword.qualityScore || 0;
        return keywordScore === selectedQualityScoreFilter;
      });
    }
    
    // Filter by match type if a filter is active
    if (selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        return keyword.matchType === selectedMatchTypeFilter;
      });
    }
    
    
    if (filteredKeywords.length === 0) {
      return {
        tiers: [],
        totalKeywords: 0,
        keywordsWithCpc: 0,
        cpcRange: { min: 0, max: 0 }
      };
    }
    
    // Optimisation pour gros datasets: traitement par chunks pour éviter stack overflow
    const CHUNK_SIZE = 10000;
    let minCpc = Infinity;
    let maxCpc = -Infinity;
    let keywordsWithCpcCount = 0;
    
    // Première passe: trouver min/max CPC par chunks
    for (let chunkStart = 0; chunkStart < filteredKeywords.length; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, filteredKeywords.length);
      const chunk = filteredKeywords.slice(chunkStart, chunkEnd);
      
      for (let i = 0; i < chunk.length; i++) {
        const keyword = chunk[i];
        const cpc = Number(keyword.cpc) || 0;
        
        if (cpc > 0) {
          keywordsWithCpcCount++;
          if (cpc < minCpc) minCpc = cpc;
          if (cpc > maxCpc) maxCpc = cpc;
        }
      }
    }
    
    if (keywordsWithCpcCount === 0) {
      return {
        tiers: [],
        totalKeywords: keywords.length,
        keywordsWithCpc: 0,
        cpcRange: { min: 0, max: 0 }
      };
    }
    
    
    // Créer 8 tranches uniformes basées sur les plages de CPC égales
    const tierCount = 8;
    const tierSize = (maxCpc - minCpc) / tierCount;
    const tiers = new Array(tierCount);
    
    // Initialiser les tranches
    for (let i = 0; i < tierCount; i++) {
      tiers[i] = {
        tier: i + 1,
        cpcMin: Number((minCpc + (i * tierSize)).toFixed(2)),
        cpcMax: Number((i === tierCount - 1 ? maxCpc : minCpc + ((i + 1) * tierSize)).toFixed(2)),
        keywordCount: 0,
        totalClicks: 0,
        totalCost: 0,
        totalConversions: 0,
        totalConversionsValue: 0
      };
    }
    
    // Deuxième passe: agrégation par chunks pour éviter les problèmes de mémoire
    for (let chunkStart = 0; chunkStart < filteredKeywords.length; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, filteredKeywords.length);
      const chunk = filteredKeywords.slice(chunkStart, chunkEnd);
      
      for (let i = 0; i < chunk.length; i++) {
        const keyword = chunk[i];
        const cpc = Number(keyword.cpc) || 0;
        
        if (cpc > 0) {
          // Trouver la tranche appropriée de manière optimisée
          let tierIndex = Math.floor((cpc - minCpc) / tierSize);
          if (tierIndex >= tierCount) tierIndex = tierCount - 1;
          if (tierIndex < 0) tierIndex = 0;
          
          const tier = tiers[tierIndex];
          tier.keywordCount++;
          tier.totalClicks += Number(keyword.clicks) || 0;
          tier.totalCost += Number(keyword.cost) || 0;
          tier.totalConversions += Number(keyword.conversions) || 0;
          tier.totalConversionsValue += Number(keyword.conversionsValue) || 0;
        }
      }
    }
    
    // Finaliser les calculs et filtrer les tranches vides
    const finalTiers = [];
    for (let i = 0; i < tierCount; i++) {
      const tier = tiers[i];
      
      if (tier.keywordCount > 0 || tier.totalClicks > 0) {
        const avgCos = tier.totalConversionsValue > 0 ? (tier.totalCost / tier.totalConversionsValue * 100) : 0;
        
        finalTiers.push({
          tier: tier.tier,
          cpcMin: tier.cpcMin,
          cpcMax: tier.cpcMax,
          keywordCount: tier.keywordCount,
          totalClicks: tier.totalClicks,
          totalCost: Number(tier.totalCost.toFixed(2)),
          totalConversions: Number(tier.totalConversions.toFixed(1)),
          totalConversionsValue: Number(tier.totalConversionsValue.toFixed(2)),
          avgCos: Number(avgCos.toFixed(1)),
          conversionRate: tier.totalClicks > 0 ? Number((tier.totalConversions / tier.totalClicks * 100).toFixed(2)) : 0,
          avgCpa: tier.totalConversions > 0 ? Number((tier.totalCost / tier.totalConversions).toFixed(2)) : 0
        });
        
      }
    }
    
    
    return {
      tiers: finalTiers,
      totalKeywords: filteredKeywords.length,
      keywordsWithCpc: keywordsWithCpcCount,
      cpcRange: {
        min: Number(minCpc.toFixed(2)),
        max: Number(maxCpc.toFixed(2))
      }
    };
  };

  const loadCpcTiers = async () => {
    if (!selectedAccount || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/keywords`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const keywordsData = response.data.data[0];
        if (keywordsData.keywords && keywordsData.keywords.length > 0) {
          // Calculer les paliers côté client
          const tiersData = calculateCpcTiers(keywordsData.keywords);
          setTierData(tiersData);
        } else {
          setTierData(null);
        }
      } else {
        setTierData(null);
      }
    } catch (err) {
      console.error('❌ [CPC-TIER-BREAKDOWN] Erreur:', err);
      setError('Erreur lors du chargement des paliers CPC');
      setTierData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Trier et filtrer les données
  const sortedTiers = React.useMemo(() => {
    if (!tierData?.tiers) return [];
    
    let filteredTiers = [...tierData.tiers];
    
    // Si un filtre CPC est actif, afficher seulement la tranche correspondante
    if (selectedCpcTierFilter && selectedCpcTierFilter.min !== undefined && selectedCpcTierFilter.max !== undefined) {
      filteredTiers = tierData.tiers.filter(tier => 
        tier.cpcMin === selectedCpcTierFilter.min && tier.cpcMax === selectedCpcTierFilter.max
      );
    }
    
    return filteredTiers.sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tierData, sortConfig, selectedCpcTierFilter]);

  useEffect(() => {
    if (selectedAccount && selectedPeriod && isVisible) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadCpcTiers();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, isVisible, selectedCampaignFilter, selectedQualityScoreFilter, selectedMatchTypeFilter]);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousPeriodData = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount || !token) {
        setPreviousTierData(null);
        return;
      }

      const previousDates = calculatePreviousPeriod();
      if (!previousDates) {
        setPreviousTierData(null);
        return;
      }

      try {
        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousDates.startDate,
          customEndDate: previousDates.endDate
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/keywords`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const keywordsData = response.data.data[0];
          if (keywordsData.keywords && keywordsData.keywords.length > 0) {
            const tiersData = calculateCpcTiers(keywordsData.keywords);
            setPreviousTierData(tiersData);
          } else {
            setPreviousTierData(null);
          }
        } else {
          setPreviousTierData(null);
        }
      } catch (err) {
        console.error('❌ [CPC-TIER-BREAKDOWN] Erreur chargement période précédente:', err);
        setPreviousTierData(null);
      }
    };

    loadPreviousPeriodData();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, token, selectedCampaignFilter, selectedQualityScoreFilter, selectedMatchTypeFilter]);

  // Formatter pour les tooltips du graphique
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p className="label" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            Tranche {data.tier}: {formatNumber(data.cpcMin, 2)}€ - {formatNumber(data.cpcMax, 2)}€
          </p>
          <p style={{ color: '#82ca9d', margin: '2px 0' }}>
            Coût: €{formatNumber(data.totalCost, 2)}
          </p>
          <p style={{ color: '#8884d8', margin: '2px 0' }}>
            Conversions: {formatNumber(data.totalConversions, 1)}
          </p>
          <p style={{ color: '#ffc658', margin: '2px 0' }}>
            COS: {formatNumber(data.avgCos, 1)}%
          </p>
          <p style={{ color: '#666', margin: '2px 0' }}>
            Mots-clés: {data.keywordCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Distribution des paliers CPC</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Chargement des paliers CPC...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !tierData || !tierData.tiers || tierData.tiers.length === 0 ? (
            <div className="empty-message">
              Aucune donnée CPC disponible pour la période sélectionnée
            </div>
          ) : (
            <>
              {/* Résumé */}
              <div style={{ 
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <strong>Mots-clés analysés:</strong> {formatNumber(tierData.totalKeywords)}
                </div>
                <div>
                  <strong>Plage de CPC:</strong> {formatNumber(tierData.cpcRange.min, 2)}€ - {formatNumber(tierData.cpcRange.max, 2)}€
                </div>
              </div>

              {/* Graphique en barres */}
              <div style={{ width: '100%', height: '400px', marginBottom: '20px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedTiers.sort((a, b) => a.tier - b.tier).map(tier => ({
                      ...tier,
                      tierLabel: `${formatNumber(tier.cpcMin, 2)}€ - ${formatNumber(tier.cpcMax, 2)}€`
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="tierLabel" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={10}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      dataKey="totalCost" 
                      fill="#82ca9d" 
                      name="Coût (€)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tableau détaillé */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('cpcMin')} className="sortable">
                        Tranches de CPC {sortConfig.key === 'cpcMin' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('keywordCount')} className="sortable numeric">
                        Mots-clés {sortConfig.key === 'keywordCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isComponentMetricSelected('clics') && (
                        <th onClick={() => handleSort('totalClicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'totalClicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <th onClick={() => handleSort('totalCost')} className="sortable numeric">
                          Coût {sortConfig.key === 'totalCost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <th onClick={() => handleSort('totalConversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'totalConversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <th onClick={() => handleSort('avgCpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'avgCpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <th onClick={() => handleSort('totalConversionsValue')} className="sortable numeric">
                          Valeur conv. {sortConfig.key === 'totalConversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <th onClick={() => handleSort('avgCos')} className="sortable numeric">
                          COS {sortConfig.key === 'avgCos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTiers.map((tier) => {
                      const cosClass = getCosClassName(tier.avgCos, selectedAccount?.id || 'global', 'keywords_cpc_tiers');
                      
                      return (
                        <tr key={tier.tier}>
                          <td 
                            onClick={() => onTierClick && onTierClick(tier.cpcMin, tier.cpcMax)}
                            style={{ 
                              cursor: onTierClick ? 'pointer' : 'default',
                              color: onTierClick ? '#333333' : 'inherit',
                              fontWeight: onTierClick ? '500' : 'normal'
                            }}
                            title={onTierClick ? 'Cliquer pour filtrer les termes de recherche par cette tranche de CPC' : ''}
                          >
                            {formatNumber(tier.cpcMin, 2)}€ - {formatNumber(tier.cpcMax, 2)}€
                          </td>
                          <td className="numeric">{formatNumber(tier.keywordCount)}</td>
                          {isComponentMetricSelected('clics') && (
                            <td className="numeric">{formatNumber(tier.totalClicks)}</td>
                          )}
                          {isComponentMetricSelected('cout') && (
                            <td className="numeric">€{formatNumber(tier.totalCost, 2)}</td>
                          )}
                          {isComponentMetricSelected('taux_conv') && (
                            <td className="numeric">{formatNumber(tier.conversionRate, 2)}%</td>
                          )}
                          {isComponentMetricSelected('conv') && (
                            <td className="numeric">{formatNumber(tier.totalConversions, 1)}</td>
                          )}
                          {isComponentMetricSelected('cpa') && (
                            <td className={`numeric ${getCpaClassName(tier.avgCpa, selectedAccount?.id || 'global', 'keywords_cpc_tiers')}`}>
                              {tier.avgCpa >= 0 ? `€${formatNumber(tier.avgCpa, 2)}` : '-'}
                            </td>
                          )}
                          {isComponentMetricSelected('valeur_conv') && (
                            <td className="numeric">€{formatNumber(tier.totalConversionsValue, 2)}</td>
                          )}
                          {isComponentMetricSelected('cos') && (
                            <td className={`numeric ${cosClass}`}>
                              {formatNumber(tier.avgCos, 1)}%
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      // Calcul des totaux actuels
                      const totalKeywordCount = sortedTiers.reduce((sum, tier) => sum + tier.keywordCount, 0);
                      const totalClicks = sortedTiers.reduce((sum, tier) => sum + tier.totalClicks, 0);
                      const totalCost = sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0);
                      const totalConversions = sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0);
                      const totalConversionsValue = sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0);
                      const totalConvRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;
                      const totalCpa = totalConversions > 0 ? (totalCost / totalConversions) : 0;
                      const totalCos = totalConversionsValue > 0 ? (totalCost / totalConversionsValue * 100) : 0;

                      // Calcul des totaux de la période précédente
                      const prevTiers = previousTierData?.tiers || [];
                      const prevTotalKeywordCount = prevTiers.reduce((sum, tier) => sum + tier.keywordCount, 0);
                      const prevTotalClicks = prevTiers.reduce((sum, tier) => sum + tier.totalClicks, 0);
                      const prevTotalCost = prevTiers.reduce((sum, tier) => sum + tier.totalCost, 0);
                      const prevTotalConversions = prevTiers.reduce((sum, tier) => sum + tier.totalConversions, 0);
                      const prevTotalConversionsValue = prevTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0);
                      const prevTotalConvRate = prevTotalClicks > 0 ? (prevTotalConversions / prevTotalClicks * 100) : 0;
                      const prevTotalCpa = prevTotalConversions > 0 ? (prevTotalCost / prevTotalConversions) : 0;
                      const prevTotalCos = prevTotalConversionsValue > 0 ? (prevTotalCost / prevTotalConversionsValue * 100) : 0;

                      return (
                        <tr className="total-row">
                          <td><strong>TOTAL</strong></td>
                          <td className="numeric">
                            <strong>{formatNumber(totalKeywordCount)}</strong>
                            {compareEnabled && previousTierData && renderVariation(calculateVariation(totalKeywordCount, prevTotalKeywordCount))}
                          </td>
                          {isComponentMetricSelected('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalClicks)}</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalClicks, prevTotalClicks))}
                            </td>
                          )}
                          {isComponentMetricSelected('cout') && (
                            <td className="numeric">
                              <strong>€{formatNumber(totalCost, 2)}</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalCost, prevTotalCost), true)}
                            </td>
                          )}
                          {isComponentMetricSelected('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalConvRate, 2)}%</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalConvRate, prevTotalConvRate))}
                            </td>
                          )}
                          {isComponentMetricSelected('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalConversions, 1)}</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalConversions, prevTotalConversions))}
                            </td>
                          )}
                          {isComponentMetricSelected('cpa') && (
                            <td className={`numeric ${getCpaClassName(totalCpa, selectedAccount?.id || 'global', 'keywords_cpc_tiers')}`}>
                              <strong>{totalConversions >= 0 ? `€${formatNumber(totalCpa, 2)}` : '-'}</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalCpa, prevTotalCpa), true)}
                            </td>
                          )}
                          {isComponentMetricSelected('valeur_conv') && (
                            <td className="numeric">
                              <strong>€{formatNumber(totalConversionsValue, 2)}</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalConversionsValue, prevTotalConversionsValue))}
                            </td>
                          )}
                          {isComponentMetricSelected('cos') && (
                            <td className={`numeric ${getCosClassName(totalCos, selectedAccount?.id || 'global', 'keywords_cpc_tiers')}`}>
                              <strong>{formatNumber(totalCos, 1)}%</strong>
                              {compareEnabled && previousTierData && renderVariation(calculateVariation(totalCos, prevTotalCos), true)}
                            </td>
                          )}
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="keywords_cpc_tiers"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Distribution CPC"
      />
    </div>
  );
};

export default CpcTierBreakdown;